import Papa from 'papaparse';
import { getCopyHTML } from './copyFormatters/indexHTML';

// ========== TYPE DEFINITIONS ==========
// Matching agent schemas from backend

interface LinkedInPost {
  content: string;
  tone: 'professional' | 'thought_leadership' | 'storytelling' | 'educational' | 'inspirational';
  hashtags: string[];
  call_to_action: string;
  engagement_hooks: string[];
}

interface LinkedInResponse {
  posts: LinkedInPost[];
}

interface Tweet {
  content: string;
  tone: 'casual' | 'professional' | 'witty' | 'informative' | 'engaging';
  hashtags: string[];
  position: number;
  thread_indicator: 'starter' | 'continuation' | 'closer' | null;
}

interface TwitterResponse {
  content_type: 'tweet' | 'thread';
  tweets: Tweet[];
}

interface NewsletterResponse {
  title: string;
  content: string;
  key_points: string[];
  target_audience: string;
  tone: 'informative' | 'friendly' | 'authoritative' | 'conversational';
  sections: {
    intro: string;
    main_content: string;
    cta: string;
    footer: string;
  };
}

interface PressReleaseResponse {
  headline: string;
  subheadline: string;
  dateline: string;
  body: string;
  boilerplate: string;
  media_contact: {
    name: string;
    title: string;
    email: string;
    phone: string;
  };
  key_points: string[];
  target_media: string[];
  news_angle: string;
}

interface SuggestedKeyword {
  keyword: string;
  volume: number;
  cpc: string;
  currency: string;
  competition: number;
}

interface SEOKeywordResponse {
  top_competitors: string[] | null;
  suggested_keywords: SuggestedKeyword[];
  seo_title: string;
  meta_description: string;
  response_text: string;
}

interface SEOContentResponse {
  query: string;
  response_text: string;
  query_url?: string | null;
  readonly_url?: string | null;
  share_url?: string | null;
  status?: string | null;
}

interface MarketingStrategistResponse {
  page_type: string;
  meta_title: string;
  meta_description: string;
  seo_keywords?: string[] | null;
  hero?: {
    h1: string;
    h2?: string | null;
    primary_cta?: string | null;
    secondary_cta?: string | null;
    visual_placeholder?: string | null;
  } | null;
  cta_variations?: string[] | null;
  sections: Array<{
    heading: string;
    subsections: Array<{
      subheading: string;
      content: string;
    }>;
  }>;
  social_proof?: any;
  credibility?: any;
  pricing?: any;
  asset_placeholders?: any;
  site_architecture?: any;
  faqs?: Array<{
    question: string;
    answer: string;
  }> | null;
  cta_final?: string | null;
  internal_links?: string[] | null;
  image_alt_texts?: string[] | null;
}

// ========== MAIN EXPORT FUNCTION ==========

interface ExportContentParams {
  agentType: string;
  content: any;
  title: string;
  createdAt: string;
  exportType: 'csv' | 'md' | 'html';
}

export function exportData({
  agentType,
  content,
  title,
  createdAt,
  exportType,
}: ExportContentParams): void {
  const cleanTitle = title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const timestamp = new Date(createdAt).toISOString().split('T')[0];
  const extensionMap: Record<string, string> = { csv: 'csv', md: 'md', html: 'html' };
  const extension = extensionMap[exportType] || 'md';
  const filename = `${cleanTitle}_${timestamp}.${extension}`;

  // For HTML export, use the existing HTML formatters (same as copy)
  if (exportType === 'html') {
    const htmlContent = getCopyHTML(agentType, content);
    // Wrap in full HTML document for proper file
    const fullHTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; color: #333; }
    h1 { color: #1a1a1a; border-bottom: 2px solid #e5e5e5; padding-bottom: 10px; }
    h2 { color: #2a2a2a; margin-top: 30px; }
    h3 { color: #3a3a3a; }
    blockquote { border-left: 4px solid #ddd; margin: 20px 0; padding-left: 20px; color: #666; font-style: italic; }
    ul, ol { padding-left: 25px; }
    li { margin: 5px 0; }
    pre { background: #f5f5f5; padding: 15px; border-radius: 5px; overflow-x: auto; }
    code { background: #f0f0f0; padding: 2px 6px; border-radius: 3px; }
    hr { border: none; border-top: 1px solid #e5e5e5; margin: 30px 0; }
    strong { color: #1a1a1a; }
    p { margin: 15px 0; }
  </style>
</head>
<body>
${htmlContent}
<hr>
<p style="color: #888; font-size: 12px;">Exported from Vibe Marketing AI on ${formatLongDate(
      createdAt,
    )}</p>
</body>
</html>`;
    downloadFile(fullHTML, filename, 'text/html');
    return;
  }

  let fileContent: string;

  switch (agentType) {
    case 'seo_keyword_agent':
      fileContent =
        exportType === 'csv'
          ? createKeywordCSV(content as SEOKeywordResponse, title, createdAt)
          : createKeywordMarkdown(content as SEOKeywordResponse, title, createdAt);
      break;

    case 'linkedin_agent':
      fileContent =
        exportType === 'csv'
          ? createLinkedInCSV(content as LinkedInResponse, title, createdAt)
          : createLinkedInMarkdown(content as LinkedInResponse, title, createdAt);
      break;

    case 'twitter_agent':
      fileContent =
        exportType === 'csv'
          ? createTwitterCSV(content as TwitterResponse, title, createdAt)
          : createTwitterMarkdown(content as TwitterResponse, title, createdAt);
      break;

    case 'newsletter_agent':
      fileContent =
        exportType === 'csv'
          ? createNewsletterCSV(content as NewsletterResponse, title, createdAt)
          : createNewsletterMarkdown(content as NewsletterResponse, title, createdAt);
      break;

    case 'press_release_agent':
      fileContent =
        exportType === 'csv'
          ? createPressReleaseCSV(content as PressReleaseResponse, title, createdAt)
          : createPressReleaseMarkdown(content as PressReleaseResponse, title, createdAt);
      break;

    case 'marketing_strategist_agent':
      fileContent =
        exportType === 'csv'
          ? createMarketingStrategyCSV(content as MarketingStrategistResponse, title, createdAt)
          : createMarketingStrategyMarkdown(
              content as MarketingStrategistResponse,
              title,
              createdAt,
            );
      break;

    case 'seo_content_agent':
      fileContent =
        exportType === 'csv'
          ? createSEOContentCSV(content as SEOContentResponse, title, createdAt)
          : createSEOContentMarkdown(content as SEOContentResponse, title, createdAt);
      break;

    default:
      fileContent =
        exportType === 'csv'
          ? createGenericCSV(content, title, createdAt)
          : createGenericMarkdown(content, title, createdAt);
  }

  downloadFile(fileContent, filename, exportType === 'csv' ? 'text/csv' : 'text/markdown');
}

// ========== UTILITY FUNCTIONS ==========

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatLongDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function arrayToCsv(data: any[]): string {
  return Papa.unparse(data, {
    quotes: true,
    header: true,
  });
}

// ========== TWITTER AGENT (Scheduler-Ready Format) ==========

function createTwitterCSV(content: TwitterResponse, title: string, createdAt: string): string {
  const rows = content.tweets.map((tweet, index) => ({
    'Tweet Number': tweet.position || index + 1,
    Content: tweet.content,
    'Character Count': tweet.content.length,
    Hashtags: tweet.hashtags.map((tag) => (tag.startsWith('#') ? tag : `#${tag}`)).join(' '),
    Tone: tweet.tone,
    'Thread Type': tweet.thread_indicator || 'single',
    'Scheduled Time': '', // Empty for user to fill
    Status: 'Draft',
  }));

  return arrayToCsv(rows);
}

function createTwitterMarkdown(content: TwitterResponse, title: string, createdAt: string): string {
  let md = `# 🐦 ${title}\n\n`;
  md += `*Generated on ${formatLongDate(createdAt)}*\n\n`;

  if (content.content_type === 'thread') {
    md += `**🧵 Twitter Thread**\n\n`;
  }

  if (content.tweets?.length > 0) {
    content.tweets.forEach((tweet, index) => {
      md += `## Tweet ${index + 1}`;
      if (tweet.thread_indicator) {
        const indicator =
          tweet.thread_indicator === 'starter'
            ? '🧵'
            : tweet.thread_indicator === 'continuation'
            ? '➡️'
            : '🏁';
        md += ` ${indicator}`;
      }
      md += `\n\n`;

      md += `**${tweet.content}**\n\n`;
      md += `*Characters: ${tweet.content.length}/280*\n\n`;

      if (tweet.hashtags?.length > 0) {
        md += `**Hashtags:** ${tweet.hashtags.join(' ')}\n\n`;
      }

      if (tweet.position && content.content_type === 'thread') {
        md += `*Thread position: ${tweet.position}*\n\n`;
      }

      md += `---\n\n`;
    });
  }

  md += `*Exported from Vibe Marketing AI*`;
  return md;
}

// ========== LINKEDIN AGENT (Scheduler-Ready Format) ==========

function createLinkedInCSV(content: LinkedInResponse, title: string, createdAt: string): string {
  const rows = content.posts.map((post, index) => ({
    'Post Number': index + 1,
    Content: post.content,
    Hashtags: post.hashtags.map((tag) => (tag.startsWith('#') ? tag : `#${tag}`)).join(' '),
    Tone: post.tone,
    'Call to Action': post.call_to_action,
    'Engagement Hooks': post.engagement_hooks.join(' | '),
    'Scheduled Time': '', // Empty for user to fill
    Status: 'Draft',
  }));

  return arrayToCsv(rows);
}

function createLinkedInMarkdown(
  content: LinkedInResponse,
  title: string,
  createdAt: string,
): string {
  let md = `# 💼 ${title}\n\n`;
  md += `*Generated on ${formatLongDate(createdAt)}*\n\n`;

  if (content.posts?.length > 0) {
    content.posts.forEach((post, index) => {
      md += `## Post ${index + 1}\n\n`;
      md += `${post.content}\n\n`;

      if (post.hashtags?.length > 0) {
        md += `**Hashtags:** ${post.hashtags.join(' ')}\n\n`;
      }

      if (post.call_to_action) {
        md += `**🎯 Call to Action:** ${post.call_to_action}\n\n`;
      }

      if (post.engagement_hooks?.length > 0) {
        md += `**💬 Engagement Hooks:**\n`;
        post.engagement_hooks.forEach((hook) => {
          md += `- ${hook}\n`;
        });
        md += `\n`;
      }

      md += `---\n\n`;
    });
  }

  md += `*Exported from Vibe Marketing AI*`;
  return md;
}

// ========== SEO KEYWORD AGENT (Analysis Table Format) ==========

function createKeywordCSV(content: SEOKeywordResponse, title: string, createdAt: string): string {
  const rows = content.suggested_keywords.map((keyword) => {
    const opportunity =
      keyword.competition < 0.3 ? 'High' : keyword.competition < 0.6 ? 'Medium' : 'Low';
    return {
      Keyword: keyword.keyword,
      'Search Volume': keyword.volume,
      CPC: `${keyword.currency}${keyword.cpc}`,
      Competition: `${(keyword.competition * 100).toFixed(0)}%`,
      Opportunity: opportunity,
      Priority: '', // Empty for user to fill
      'Content Status': '', // Empty for user to fill
    };
  });

  return arrayToCsv(rows);
}

function createKeywordMarkdown(
  content: SEOKeywordResponse,
  title: string,
  createdAt: string,
): string {
  let md = `# 🔍 ${title}\n\n`;
  md += `*Generated on ${formatLongDate(createdAt)}*\n\n`;

  if (content.seo_title) {
    md += `## 📝 SEO Title\n\n`;
    md += `**${content.seo_title}**\n\n`;
  }

  if (content.meta_description) {
    md += `## 📄 Meta Description\n\n`;
    md += `> ${content.meta_description}\n\n`;
  }

  if (content.suggested_keywords?.length > 0) {
    md += `## 🎯 Keyword Opportunities\n\n`;
    md += `| Keyword | Volume | CPC | Competition | Opportunity |\n`;
    md += `|---------|--------|-----|-------------|------------|\n`;

    content.suggested_keywords.forEach((keyword) => {
      const opportunity =
        keyword.competition < 0.3
          ? '🟢 **High**'
          : keyword.competition < 0.6
          ? '🟡 **Medium**'
          : '🔴 **Low**';
      md += `| **${keyword.keyword}** | ${keyword.volume.toLocaleString()} | ${keyword.currency}${
        keyword.cpc
      } | ${(keyword.competition * 100).toFixed(0)}% | ${opportunity} |\n`;
    });
  }

  if (content.top_competitors && content.top_competitors.length > 0) {
    md += `\n## 🏆 Top Competitors\n\n`;
    content.top_competitors.forEach((competitor, index) => {
      md += `${index + 1}. **${competitor}**\n`;
    });
    md += `\n> 💡 **Tip:** Analyze these competitors' keyword strategies and content gaps.\n\n`;
  }

  if (content.response_text) {
    md += `## 📊 Analysis\n\n`;
    md += `${content.response_text}\n\n`;
  }

  md += `\n---\n\n*Exported from Vibe Marketing AI*`;
  return md;
}

// ========== SEO CONTENT AGENT (FIXED - Matches Actual Schema) ==========

function createSEOContentCSV(
  content: SEOContentResponse,
  title: string,
  createdAt: string,
): string {
  const rows = [
    {
      'Query ID': content.query,
      Status: content.status || 'Completed',
      'Query URL': content.query_url || '',
      'Read-Only URL': content.readonly_url || '',
      'Share URL': content.share_url || '',
      'Generated Date': formatDate(createdAt),
    },
  ];

  return arrayToCsv(rows);
}

function createSEOContentMarkdown(
  content: SEOContentResponse,
  title: string,
  createdAt: string,
): string {
  let md = `# 🔍 ${title}\n\n`;
  md += `*Generated on ${formatLongDate(createdAt)}*\n\n`;

  if (content.query) {
    md += `## 📋 Query Information\n\n`;
    md += `**Query ID:** ${content.query}\n\n`;

    if (content.status) {
      md += `**Status:** ${content.status}\n\n`;
    }
  }

  if (content.query_url || content.readonly_url || content.share_url) {
    md += `## 🔗 Links\n\n`;
    if (content.query_url) md += `- [Query URL](${content.query_url})\n`;
    if (content.readonly_url) md += `- [Read-Only Preview](${content.readonly_url})\n`;
    if (content.share_url) md += `- [Share URL](${content.share_url})\n`;
    md += `\n`;
  }

  if (content.response_text) {
    md += `## 📊 Analysis\n\n`;
    md += `${content.response_text}\n\n`;
  }

  md += `---\n\n*Exported from Vibe Marketing AI*`;
  return md;
}

// ========== NEWSLETTER AGENT (Email Platform Format) ==========

function createNewsletterCSV(
  content: NewsletterResponse,
  title: string,
  createdAt: string,
): string {
  const rows = [
    {
      Section: 'Subject Line',
      Content: content.title || title,
      'Word Count': content.title?.split(' ').length || 0,
      CTA: '',
    },
    {
      Section: 'Introduction',
      Content: content.sections.intro,
      'Word Count': content.sections.intro.split(' ').length,
      CTA: '',
    },
    {
      Section: 'Main Content',
      Content: content.sections.main_content,
      'Word Count': content.sections.main_content.split(' ').length,
      CTA: content.sections.cta,
    },
    {
      Section: 'Footer',
      Content: content.sections.footer,
      'Word Count': content.sections.footer.split(' ').length,
      CTA: '',
    },
  ];

  return arrayToCsv(rows);
}

function createNewsletterMarkdown(
  content: NewsletterResponse,
  title: string,
  createdAt: string,
): string {
  let md = `# 📧 ${title}\n\n`;
  md += `*Generated on ${formatLongDate(createdAt)}*\n\n`;

  if (content.title) {
    md += `## 📋 Subject Line\n\n`;
    md += `**${content.title}**\n\n`;
  }

  if (content.target_audience) {
    md += `## 👥 Target Audience\n\n`;
    md += `${content.target_audience}\n\n`;
  }

  if (content.key_points?.length > 0) {
    md += `## 🎯 Key Points\n\n`;
    content.key_points.forEach((point) => {
      md += `- ${point}\n`;
    });
    md += `\n`;
  }

  if (content.sections?.intro) {
    md += `## 📖 Introduction\n\n`;
    md += `${content.sections.intro}\n\n`;
  }

  if (content.sections?.main_content) {
    md += `## 📝 Main Content\n\n`;
    md += `${content.sections.main_content}\n\n`;
  }

  if (content.sections?.cta) {
    md += `## 🎯 Call to Action\n\n`;
    md += `> **${content.sections.cta}**\n\n`;
  }

  if (content.sections?.footer) {
    md += `## 📄 Footer\n\n`;
    md += `*${content.sections.footer}*\n\n`;
  }

  md += `---\n\n*Exported from Vibe Marketing AI*`;
  return md;
}

// ========== PRESS RELEASE AGENT (Media Contact Format) ==========

function createPressReleaseCSV(
  content: PressReleaseResponse,
  title: string,
  createdAt: string,
): string {
  // Media Contact Info Table
  const mediaContact = [
    {
      'Contact Name': content.media_contact?.name || '',
      Title: content.media_contact?.title || '',
      Email: content.media_contact?.email || '',
      Phone: content.media_contact?.phone || '',
    },
  ];

  let csv = `Media Contact Information\n`;
  csv += arrayToCsv(mediaContact);

  // Target Media Outlets Table
  if (content.target_media?.length > 0) {
    csv += `\n\nTarget Media Outlets\n`;
    const outlets = content.target_media.map((outlet, index) => ({
      Priority: index + 1,
      'Outlet/Journalist': outlet,
      Contacted: '', // Empty for user to track
      Response: '', // Empty for user to track
    }));
    csv += arrayToCsv(outlets);
  }

  // Key Points Table
  if (content.key_points?.length > 0) {
    csv += `\n\nKey Points\n`;
    const points = content.key_points.map((point, index) => ({
      'Point #': index + 1,
      'Key Message': point,
    }));
    csv += arrayToCsv(points);
  }

  return csv;
}

function createPressReleaseMarkdown(
  content: PressReleaseResponse,
  title: string,
  createdAt: string,
): string {
  let md = `# 📰 ${title}\n\n`;
  md += `*Generated on ${formatLongDate(createdAt)}*\n\n`;

  md += `## 📰 FOR IMMEDIATE RELEASE\n\n`;

  if (content.headline) {
    md += `## ${content.headline.toUpperCase()}\n\n`;
  }

  if (content.subheadline) {
    md += `### ${content.subheadline}\n\n`;
  }

  if (content.dateline) {
    md += `**${content.dateline}**\n\n`;
  }

  if (content.key_points?.length > 0) {
    md += `## 🎯 Key Highlights\n\n`;
    content.key_points.forEach((point) => {
      md += `- ${point}\n`;
    });
    md += `\n`;
  }

  if (content.body) {
    md += `## 📝 Press Release Body\n\n`;
    md += `${content.body}\n\n`;
  }

  if (content.boilerplate) {
    md += `### About\n\n`;
    md += `${content.boilerplate}\n\n`;
  }

  if (content.media_contact) {
    md += `## 📧 Media Contact\n\n`;
    md += `**${content.media_contact.name}**\n`;
    if (content.media_contact.title) md += `${content.media_contact.title}\n`;
    if (content.media_contact.email) md += `📧 ${content.media_contact.email}\n`;
    if (content.media_contact.phone) md += `📞 ${content.media_contact.phone}\n`;
    md += `\n`;
  }

  if (content.target_media?.length > 0) {
    md += `## 🎯 Target Media\n\n`;
    content.target_media.forEach((outlet) => {
      md += `- **${outlet}**\n`;
    });
    md += `\n`;
  }

  if (content.news_angle) {
    md += `## 📈 News Angle\n\n`;
    md += `${content.news_angle}\n\n`;
  }

  md += `###\n\n*Exported from Vibe Marketing AI*`;
  return md;
}

// ========== MARKETING STRATEGIST AGENT (FIXED - Matches Actual Schema) ==========

function createMarketingStrategyCSV(
  content: MarketingStrategistResponse,
  title: string,
  createdAt: string,
): string {
  // Different CSV formats based on page_type
  switch (content.page_type) {
    case 'website_structure':
      return createWebsiteStructureCSV(content);

    case 'positioning_document':
      return createPositioningDocumentCSV(content);

    case 'landing_page':
    case 'homepage':
    case 'product':
    case 'about':
    case 'blog_post':
    default:
      return createLandingPageCSV(content);
  }
}

// Website Structure Format - Page Architecture Table
function createWebsiteStructureCSV(content: MarketingStrategistResponse): string {
  if (!content.site_architecture?.pages) {
    return createLandingPageCSV(content); // Fallback
  }

  const rows = content.site_architecture.pages.map((page: any) => ({
    'Page Name': page.page_name,
    'URL Slug': page.url_slug,
    Purpose: page.page_purpose,
    'H1 Heading': page.h1 || page.content_hierarchy?.h1 || '',
    'Section Count': (page.h2_sections || page.content_hierarchy?.h2_sections || []).length,
    Status: '', // Empty for user to track
    Notes: '',
  }));

  return arrayToCsv(rows);
}

// Positioning Document Format - Full Content, No Truncation
function createPositioningDocumentCSV(content: MarketingStrategistResponse): string {
  const rows: any[] = [];

  // Add all sections with full content
  if (content.sections?.length > 0) {
    content.sections.forEach((section) => {
      section.subsections.forEach((subsection) => {
        rows.push({
          Section: section.heading,
          Subsection: subsection.subheading,
          Content: subsection.content, // FULL content, no truncation
        });
      });
    });
  }

  return arrayToCsv(rows);
}

// Landing Page Format - Content Planning Table
function createLandingPageCSV(content: MarketingStrategistResponse): string {
  const rows: any[] = [];

  // Meta Information
  rows.push({
    'Section Type': 'Meta',
    Section: 'SEO Title',
    Content: content.meta_title,
    'Word Count': content.meta_title.split(' ').length,
    Status: '',
    Priority: 'High',
  });

  rows.push({
    'Section Type': 'Meta',
    Section: 'Meta Description',
    Content: content.meta_description,
    'Word Count': content.meta_description.split(' ').length,
    Status: '',
    Priority: 'High',
  });

  // Hero Section
  if (content.hero) {
    rows.push({
      'Section Type': 'Hero',
      Section: 'Headline (H1)',
      Content: content.hero.h1,
      'Word Count': content.hero.h1.split(' ').length,
      Status: '',
      Priority: 'High',
    });

    if (content.hero.h2) {
      rows.push({
        'Section Type': 'Hero',
        Section: 'Subheadline (H2)',
        Content: content.hero.h2,
        'Word Count': content.hero.h2.split(' ').length,
        Status: '',
        Priority: 'High',
      });
    }

    if (content.hero.primary_cta) {
      rows.push({
        'Section Type': 'Hero',
        Section: 'Primary CTA',
        Content: content.hero.primary_cta,
        'Word Count': '-',
        Status: '',
        Priority: 'High',
      });
    }
  }

  // CTA Variations
  if (content.cta_variations?.length) {
    content.cta_variations.forEach((cta, index) => {
      rows.push({
        'Section Type': 'CTA Variation',
        Section: `A/B Test Option ${index + 1}`,
        Content: cta,
        'Word Count': '-',
        Status: '',
        Priority: 'Medium',
      });
    });
  }

  // Main Content Sections
  if (content.sections?.length > 0) {
    content.sections.forEach((section, sectionIndex) => {
      // Add section heading
      rows.push({
        'Section Type': 'Content',
        Section: `${section.heading} (H2)`,
        Content: `[Section ${sectionIndex + 1}]`,
        'Word Count': '-',
        Status: '',
        Priority: 'High',
      });

      // Add subsections
      section.subsections.forEach((subsection) => {
        rows.push({
          'Section Type': 'Content',
          Section: `  └─ ${subsection.subheading} (H3)`,
          Content: subsection.content,
          'Word Count': subsection.content.split(' ').length,
          Status: '',
          Priority: 'Medium',
        });
      });
    });
  }

  // Social Proof Stats
  if (content.social_proof?.stats?.length) {
    content.social_proof.stats.forEach((stat: any) => {
      rows.push({
        'Section Type': 'Social Proof',
        Section: 'Stat',
        Content: `${stat.number} - ${stat.label}`,
        'Word Count': '-',
        Status: '',
        Priority: 'High',
      });
    });
  }

  // Pricing Plans
  if (content.pricing?.length) {
    content.pricing.forEach((plan: any, index: number) => {
      rows.push({
        'Section Type': 'Pricing',
        Section: `${plan.plan_name}${plan.highlight ? ' ⭐' : ''}`,
        Content: `${plan.price} - ${plan.features.length} features`,
        'Word Count': '-',
        Status: '',
        Priority: plan.highlight ? 'High' : 'Medium',
      });
    });
  }

  // FAQs
  if (content.faqs && content.faqs.length > 0) {
    content.faqs.forEach((faq, index) => {
      rows.push({
        'Section Type': 'FAQ',
        Section: `Q${index + 1}: ${faq.question}`,
        Content: faq.answer,
        'Word Count': faq.answer.split(' ').length,
        Status: '',
        Priority: 'Medium',
      });
    });
  }

  // Final CTA
  if (content.cta_final) {
    rows.push({
      'Section Type': 'Final CTA',
      Section: 'Closing Call to Action',
      Content: content.cta_final,
      'Word Count': '-',
      Status: '',
      Priority: 'High',
    });
  }

  // Asset Requirements
  if (content.asset_placeholders?.length) {
    content.asset_placeholders.forEach((asset: any) => {
      rows.push({
        'Section Type': 'Asset Needed',
        Section: `${asset.type} (${asset.section})`,
        Content: asset.description,
        'Word Count': '-',
        Status: '',
        Priority: 'Medium',
      });
    });
  }

  return arrayToCsv(rows);
}

function createMarketingStrategyMarkdown(
  content: MarketingStrategistResponse,
  title: string,
  createdAt: string,
): string {
  let md = `# 🎯 ${title}\n\n`;
  md += `*Generated on ${formatLongDate(createdAt)}*\n\n`;

  // Page Type & SEO
  md += `## 📋 Page Details\n\n`;
  md += `**Page Type:** ${content.page_type}\n\n`;
  md += `**SEO Title:** ${content.meta_title}\n\n`;
  md += `**Meta Description:** ${content.meta_description}\n\n`;

  if (content.seo_keywords?.length) {
    md += `**SEO Keywords:** ${content.seo_keywords.join(', ')}\n\n`;
  }

  // Hero Section
  if (content.hero) {
    md += `## 🎯 Hero Section\n\n`;
    md += `### ${content.hero.h1}\n\n`;
    if (content.hero.h2) md += `${content.hero.h2}\n\n`;
    if (content.hero.primary_cta) md += `**Primary CTA:** ${content.hero.primary_cta}\n\n`;
    if (content.hero.secondary_cta) md += `**Secondary CTA:** ${content.hero.secondary_cta}\n\n`;
    if (content.hero.visual_placeholder) md += `**Visual:** ${content.hero.visual_placeholder}\n\n`;
  }

  // CTA Variations
  if (content.cta_variations?.length) {
    md += `## 🎯 CTA Variations for A/B Testing\n\n`;
    content.cta_variations.forEach((cta, index) => {
      md += `${index + 1}. ${cta}\n`;
    });
    md += `\n`;
  }

  // Main Content Sections
  if (content.sections?.length > 0) {
    md += `## 📝 Main Content\n\n`;
    content.sections.forEach((section) => {
      md += `### ${section.heading}\n\n`;
      section.subsections.forEach((subsection) => {
        md += `#### ${subsection.subheading}\n\n`;
        md += `${subsection.content}\n\n`;
      });
    });
  }

  // Social Proof
  if (content.social_proof) {
    md += `## 🏆 Social Proof\n\n`;
    if (content.social_proof.testimonials?.length) {
      md += `### Testimonials\n\n`;
      content.social_proof.testimonials.forEach((testimonial: any) => {
        md += `> "${testimonial.quote}"\n>\n> — **${testimonial.author}**, ${testimonial.role}\n\n`;
      });
    }
    if (content.social_proof.stats?.length) {
      md += `### Key Stats\n\n`;
      content.social_proof.stats.forEach((stat: any) => {
        md += `- **${stat.number}** ${stat.label}\n`;
      });
      md += `\n`;
    }
  }

  // Pricing
  if (content.pricing?.length) {
    md += `## 💰 Pricing\n\n`;
    content.pricing.forEach((plan: any) => {
      md += `### ${plan.plan_name}${plan.highlight ? ' ⭐ RECOMMENDED' : ''}\n\n`;
      md += `**${plan.price}**\n\n`;
      md += `**Features:**\n`;
      plan.features.forEach((feature: string) => {
        md += `- ${feature}\n`;
      });
      md += `\n**CTA:** ${plan.cta}\n\n`;
    });
  }

  // FAQs
  if (content.faqs && content.faqs.length > 0) {
    md += `## ❓ FAQs\n\n`;
    content.faqs.forEach((faq) => {
      md += `### ${faq.question}\n\n`;
      md += `${faq.answer}\n\n`;
    });
  }

  // Final CTA
  if (content.cta_final) {
    md += `## 🚀 Final Call to Action\n\n`;
    md += `**${content.cta_final}**\n\n`;
  }

  // Asset Placeholders
  if (content.asset_placeholders?.length) {
    md += `## 🖼️ Asset Requirements\n\n`;
    content.asset_placeholders.forEach((asset: any) => {
      md += `- **${asset.type}** (${asset.section}): ${asset.description}\n`;
    });
    md += `\n`;
  }

  // SEO Elements
  if (content.internal_links?.length || content.image_alt_texts?.length) {
    md += `## 🔍 SEO Elements\n\n`;
    if (content.internal_links?.length) {
      md += `**Internal Links:**\n`;
      content.internal_links.forEach((link) => {
        md += `- ${link}\n`;
      });
      md += `\n`;
    }
    if (content.image_alt_texts?.length) {
      md += `**Image Alt Texts:**\n`;
      content.image_alt_texts.forEach((alt) => {
        md += `- ${alt}\n`;
      });
      md += `\n`;
    }
  }

  md += `---\n\n*Exported from Vibe Marketing AI*`;
  return md;
}

// ========== FALLBACKS ==========

function createGenericCSV(content: any, title: string, createdAt: string): string {
  const rows = [
    {
      Title: title,
      Generated: formatDate(createdAt),
      Content: JSON.stringify(content, null, 2).substring(0, 1000) + '...',
    },
  ];
  return arrayToCsv(rows);
}

function createGenericMarkdown(content: any, title: string, createdAt: string): string {
  return `# ${title}\n\n*Generated on ${formatDate(createdAt)}*\n\n\`\`\`json\n${JSON.stringify(
    content,
    null,
    2,
  )}\n\`\`\``;
}

// ========== UTILITY ==========

function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
